// @ts-check
/**
 * Calls `callback` when 'DOMContentLoaded' happens.
 *
 * @param {() => void} callback
 */
export function onDOMReady(callback) {
  if (document.readyState !== 'loading') {
    callback();
  } else {
    document.addEventListener('DOMContentLoaded', callback, { once: true });
  }
}

/**
 * Returns element height value. Equal to `$(element).height();`.
 *
 * @param {Element} element
 * @returns {number}
 */
export function getElementHeight(element) {
  return element
    ? parseFloat(getComputedStyle(element, null).height.replace('px', ''))
    : 0;
}

/**
 * @typedef {object} ScrollToOptions
 * https://developer.mozilla.org/en-US/docs/Web/API/ScrollToOptions
 * @property {number} top Specifies the number of pixels along the Y axis to scroll the window or element.
 * @property {number} left Specifies the number of pixels along the X axis to scroll the window or element.
 * @property {ScrollBehavior} behavior Specifies whether the scrolling should animate smoothly,
 * or happen instantly in a single jump.
 */

/**
 * @typedef {object} ScrollToExtendedOptions
 * @property {ScrollToOptions} options Properties specifying where an element should be scrolled to,
 * and whether the scrolling should be smooth.
 * @property {Element} scrollContainer
 * @property {Function} callback Function to be invoked after the scrolling was finished.
 */

/**
 * Create scrollTo with callback.
 *
 * @param {ScrollToExtendedOptions} options
 */
export function scrollToWithCallback({ options, scrollContainer = window, callback }) {
  const scrollOptions = {
    top: 0, left: 0, behavior: 'smooth', ...options
  };
  const { top, left } = scrollOptions;

  const onScroll = function () {
    if (scrollContainer.scrollTop === Math.floor(top) && scrollContainer.scrollLeft === Math.floor(left)) {
      scrollContainer.removeEventListener('scroll', onScroll);
      callback();
    }
  };

  scrollContainer.addEventListener('scroll', onScroll);
  onScroll();
  scrollContainer.scrollTo(scrollOptions);
}

/**
 * @deprecated `dom4` adds `new Event()` support for IE11
 * `document.createEvent` => `new Event()` API bridge.
 * We have to use `createEvent` because IE11 doesn't support `new Event()`.
 *
 * @param {string} eventName
 * @param {object} [eventInit]
 * @param {boolean} [eventInit.bubbles=false] Whether the event bubbles.
 * @param {boolean} [eventInit.cancelable=false] Whether the event can be cancelled.
 * @returns {Event}
 */
export function createEvent(eventName, { bubbles = false, cancelable = false } = {}) {
  const event = document.createEvent('Event');

  event.initEvent(eventName, bubbles, cancelable);

  return event;
}

/**
 * @typedef {(e: Event, delegateTarget: EventTarget) => void} DelegateEventCallback
 */

/**
 * Call `callback` if event target matches selector.
 *
 * @param {DelegateEventCallback} callback
 * @param {Event} e
 * @param {string} selector
 */
function callIfTargetMatches(callback, e, selector) {
  if (!e.target) {
    return;
  }

  if (e.target.matches(selector)) {
    callback(e, e.target);
    return;
  }

  // Use `closest` to handle cases when click happens on element inside `selector` element
  const delegateTarget = e.target.closest(selector);

  if (delegateTarget) {
    callback(e, delegateTarget);
  }
}

/**
 * Event delegation.
 *
 * @param {Element | Document} subject Container.
 * @param {string} type Event type.
 * @param {string} selector Target element selector.
 * @param {DelegateEventCallback} callback
 * @param {AddEventListenerOptions} [options]
 * @returns {() => void} Remove event listener function.
 */
export function delegateEvent(subject, type, selector, callback, options) {
  /** @type {(e: Event) => void} */
  const eventCallback = (e) => {
    callIfTargetMatches(callback, e, selector);
  };

  /** @type {AddEventListenerOptions} */
  const addEventListenerOptions = { ...(options || {}), capture: true };

  subject.addEventListener(type, eventCallback, addEventListenerOptions);

  return function removeEventListener() {
    subject.removeEventListener(type, eventCallback, addEventListenerOptions);
  };
}

/**
 * Event delegation.
 *
 * @param {Element | Document} subject Container.
 * @param {string} type Event type.
 * @param { { [selector: string]: DelegateEventCallback } } selectorToCallbackMap Object like
 * { '.selector': (e) => { ... } }.
 * @returns {() => void} Remove event listener function.
 *
 * @example
 * ```
 * delegateEvents(container, 'click', {
 *   '.next-link': moveToNext(),
 *   '.prev-link': moveToPrevious()
 * })
 * ```
 */
export function delegateEvents(subject, type, selectorToCallbackMap) {
  const selectorCallbackPairs = Object.entries(selectorToCallbackMap);

  /** @type {(e: Event) => void} */
  const eventCallback = (e) => {
    selectorCallbackPairs.forEach(([selector, callback]) => {
      callIfTargetMatches(callback, e, selector);
    });
  };

  subject.addEventListener(type, eventCallback);

  return function removeEventListener() {
    subject.removeEventListener(type, eventCallback);
  };
}

/**
 * Trigger element reflow. Use to split styles applying in two phases so that changes before `forceReflow` call are
 * effective after the call.
 * Read more about reflows at https://gist.github.com/paulirish/5d52fb081b3570c81e3a.
 *
 * @param {HTMLElement} element
 */
export function forceReflow(element) {
  // eslint-disable-next-line no-unused-expressions
  element.offsetHeight;
}
